import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardContent,
  CardHeader,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import {
  toastSuccess,
  toastError,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { useState } from 'react';
import { OPERATION_META, OPERATION_KEYS } from '@Constants/operations';
import {
  useAgentOperations,
  useAssignOperation,
  useRemoveOperation,
} from '@Hooks/useAgentOperations';

const SELECT_CLASS =
  'border-input focus-visible:border-ring focus-visible:ring-ring/50 flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:ring-[3px]';

interface Props {
  soulId: string;
}

export default function AgentOperationsCard({ soulId }: Props) {
  const { data: operations, status } = useAgentOperations({ soulId });
  const assign = useAssignOperation(soulId);
  const remove = useRemoveOperation(soulId);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState('');

  const owned = new Set((operations ?? []).map((o) => o.operation));
  const available = OPERATION_KEYS.filter((k) => !owned.has(k));

  const handleAssign = async () => {
    if (!selected) return;
    try {
      await assign.mutateAsync(selected);
      setSelected('');
      setOpen(false);
      toastSuccess('Operation assigned.');
    } catch (e: any) {
      toastError(`Failed: ${e?.message ?? e}`);
    }
  };

  const handleRemove = async (id: string) => {
    try {
      await remove.mutateAsync(id);
      toastSuccess('Operation removed.');
    } catch (e: any) {
      toastError(`Remove failed: ${e?.message ?? e}`);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <Heading level="h3">Operations</Heading>
          <p className="text-xs text-zinc-500">
            What this agent owns and runs. Drives the agent's desk.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" disabled={!available.length}>
              Assign Operation
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Assign Operation</DialogTitle>
            </DialogHeader>
            <select
              className={SELECT_CLASS}
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            >
              <option value="">Select an operation…</option>
              {available.map((key) => (
                <option key={key} value={key}>
                  {OPERATION_META[key]?.label ?? key}
                </option>
              ))}
            </select>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="link">Cancel</Button>
              </DialogClose>
              <Button
                onClick={handleAssign}
                loading={assign.isPending}
                disabled={!selected}
              >
                Assign
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {status === 'pending' ? (
          <div className="py-4 text-sm text-zinc-500">Loading…</div>
        ) : !operations?.length ? (
          <div className="py-4 text-sm text-zinc-500">
            No operations assigned.
          </div>
        ) : (
          <div className="space-y-2">
            {operations.map((op) => (
              <div
                key={op.id}
                className="flex items-center justify-between rounded-md border p-3"
              >
                <span className="font-medium">
                  {OPERATION_META[op.operation]?.label ?? op.operation}
                </span>
                <Button
                  variant="dangerLink"
                  size="sm"
                  onClick={() => handleRemove(op.id)}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
